import { CustomNoResultsOverlay, CustomNoRowsOverlay } from './NoRowsComponent';
import CustomColumnsPanel from './CustomColumnsPanel';
import { useState, useRef, useEffect } from 'react';
import {
    Paper,
    Button,
    Dialog,
    DialogContent,
    Typography,
    CircularProgress,
    Tabs,
    Tab
} from '@mui/material';
import {
    DataGrid,
    GridToolbar,
} from '@mui/x-data-grid';
import {
    Visibility,
    KeyboardArrowLeft
} from '@mui/icons-material';
import Styles from './StudentsResultsTable.module.scss'
import { useNavigate, useParams } from 'react-router-dom';
import AssessmentReportCard from '../AssessmentReportCard';
import axios from 'axios';

export const StudentResultsTable = () => {
    const { diyQuizId, assessmentId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    
    const [error, setError] = useState('')
    const [showReportDialog, setShowReportDialog] = useState(false);
    const [reportDetails, setReportDetails] = useState(false);
    const [reportLoading, setReportLoading] = useState(false);

    const [studentsData, setStudentsData] = useState({
        columns: [],
        data: [],
        columnGroups: []
    })

    // State for mode tabs
    const [selectedMode, setSelectedMode] = useState(0); // 0 for Live, 1 for Test

    const [paginationModel, setPaginationModel] = useState({
        page: 0,          // DataGrid uses 0-based indexing
        pageSize: 25,     // Your default rows per page
    });

    // State for sorting
    const [sortModel, setSortModel] = useState([]);

    // State for filtering
    const [filterModel, setFilterModel] = useState({
        quickFilterValues: [],
        items: [],
    });

    //Total Items available in the data for table
    const [totalItems, setTotalItems] = useState(0);


    const fetchStudentData = async (options = {}) => {
        const {
            paginationModel: pagination = paginationModel,
            sortModel: sorting = sortModel,
            filterModel: filtering = filterModel
        } = options;

        setLoading(true);
        setError(null);

        try {
            // Prepare sorting parameters for the API
            const sortField = sorting.length > 0 ? sorting[0].field : '';
            const sortDirection = sorting.length > 0 ? sorting[0].sort : '';

            // Get search query from quick filter
            const search = filtering.quickFilterValues?.length > 0
                ? filtering.quickFilterValues.join(' ')
                : '';

            const response = await axios.get(
                `/api/diy-quiz/${diyQuizId}/assessments/${assessmentId}/results`,
                {
                    params: {
                        pageNumber: pagination.page + 1,  // Convert to 1-based for API
                        pageSize: pagination.pageSize,
                        search: search.trim(),
                        is_test_mode: selectedMode,
                        sortBy: sortField,
                        sortOrder: sortDirection
                    }
                }
            );

            const { pagination: paginationData, ...rest } = response.data;
            setStudentsData(rest);
            setTotalItems(paginationData.totalItems);
        } catch (error) {
            console.error('Error fetching student data:', error);
            setError('Failed to load student data. Please try again.');
        } finally {
            setStudentsData((prevData) => {
                const hasActionsColumn = prevData.columns.some(col => col.field === 'actions');

                return {
                    ...prevData,
                    columns: [
                        ...prevData.columns.map(col => ({
                            ...col,
                            align: 'center',
                            headerAlign: 'center'
                        })),
                        ...(!hasActionsColumn
                            ? [{
                                field: 'actions',
                                headerName: 'Report',
                                flex: 1,
                                minWidth: 150,
                                align: 'center',
                                headerAlign: 'center',
                                hideable: false,
                                sortable: false,
                                filterable:false,
                                renderCell: (params) => (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<Visibility />}
                                        onClick={() => handleViewReport(params.row)}
                                        className={Styles.viewButton}
                                    >
                                        View
                                    </Button>
                                )
                            }]
                            : [])
                    ]
                };
            });
            setLoading(false);
        }
    };

    useEffect(() => {
        const resetPaginationModel = { ...paginationModel, page: 0 };
        setPaginationModel(resetPaginationModel);
        fetchStudentData({ paginationModel: resetPaginationModel });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assessmentId, diyQuizId, selectedMode]);

    const handleModeChange = (event, newValue) => {
        setSelectedMode(newValue);
        const newPaginationModel = { ...paginationModel, page: 0 };
        setPaginationModel(newPaginationModel);
    };

    // Add a debounce timeout ref
    const searchDebounceRef = useRef(null);

    // Update filter model change handler with debouncing
    const handleFilterModelChange = (newFilterModel) => {
        // Clear any pending debounce
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }

        // Update the filter model state immediately for UI responsiveness
        setFilterModel(newFilterModel);

        // Debounce the actual API call
        searchDebounceRef.current = setTimeout(() => {
            // Reset to first page when filter changes
            const newPaginationModel = { ...paginationModel, page: 0 };
            setPaginationModel(newPaginationModel);

            // Now fetch data with the debounced filter
            fetchStudentData({
                paginationModel: newPaginationModel,
                filterModel: newFilterModel
            });
        }, 1000);
    };

    // Make sure to clean up the debounce timer
    useEffect(() => {
        return () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
        };
    }, []);

    const handleViewReport = async (student) => {
        setShowReportDialog(true)
        setReportLoading(true)
        try {
            const response = await axios.get(
                `/api/diy-quiz/${diyQuizId}/assessments/${assessmentId}/report`,
                {
                    params: {
                        email: student.email,
                        is_test_mode: selectedMode // Add test mode parameter
                    }
                }
            );
            if (response?.data?.data) {
                setReportDetails(response.data.data)
            } else {
                setError('Failed to fetch report.')
            }
        } catch (err) {
            setError('Failed to fetch report. Try again later');
            console.log('Failed to fetch report: ', err.message);
        } finally {
            setReportLoading(false)
        }
    };

    return (
        <div id='ancestorContainer' className={Styles.ancestorContainer}>
            <div id='backContainer' className={Styles.backButtonContainer}>
                <Button
                    startIcon={<KeyboardArrowLeft fontSize='small' />}
                    variant='text'
                    onClick={() => { navigate(-1) }}
                >
                    Back to Assessments
                </Button>
            </div>
            <div id='headerContainer' className={Styles.headerContainer}>
                <Typography variant='h5'>
                    Participant Reports
                </Typography>
            </div>
            <div id='tableContainer' className={Styles.tableContainer}>
                {/* Add Tabs for Live and Test Mode */}
                <Paper elevation={1} className={Styles.modeTabsPaper}>
                    <Tabs
                        value={selectedMode}
                        onChange={handleModeChange}
                        aria-label="mode tabs"
                        className={Styles.modeTabs}
                        centered
                    >
                        <Tab label="Live Mode" />
                        <Tab label="Test Mode" />
                    </Tabs>
                </Paper>

                <div className={Styles.tableContainer}>
                    {loading ? (
                        <div className={Styles.loadingContainer}>
                            <CircularProgress />
                        </div>
                    ) : (
                        <DataGrid
                            rows={studentsData.data}
                            columns={studentsData.columns}
                            columnGroupingModel={studentsData.columnGroups}
                            initialState={{
                                columns: {
                                    columnVisibilityModel: {
                                        // Hide column id 
                                        id: false,
                                    },
                                },
                                pagination: { paginationModel: { pageSize: paginationModel.pageSize } }
                            }}
                            // Pagination props
                            paginationModel={paginationModel}
                            onPaginationModelChange={(newModel) => {
                                setPaginationModel(newModel);
                                fetchStudentData({ paginationModel: newModel });
                            }}
                            pageSizeOptions={[25, 50, 100, 500, { value: totalItems, label: 'All' }]}
                            rowCount={totalItems}
                            paginationMode="server"

                            // Sorting props
                            sortModel={sortModel}
                            onSortModelChange={(newSortModel) => {
                                setSortModel(newSortModel);
                                // Reset to first page when sorting changes
                                const newPaginationModel = { ...paginationModel, page: 0 };
                                setPaginationModel(newPaginationModel);
                                fetchStudentData({
                                    paginationModel: newPaginationModel,
                                    sortModel: newSortModel
                                });
                            }}
                            sortingMode="server"

                            // Filter props
                            filterModel={filterModel}
                            onFilterModelChange={handleFilterModelChange}
                            filterMode="server"
                            loading={loading}
                            slots={{
                                toolbar: GridToolbar,
                                noRowsOverlay: CustomNoRowsOverlay,
                                noResultsOverlay: CustomNoResultsOverlay,
                                columnsPanel: CustomColumnsPanel
                            }}
                            hideFooterSelectedRowCount
                            slotProps={{
                                loadingOverlay: {
                                    variant: 'skeleton',
                                    noRowsVariant: 'skeleton',
                                },
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: {
                                        debounceMs: 0,
                                    },
                                },
                            }}
                            disableSelectionOnClick
                            disableColumnFilter 
                            sx={{
                                '& .MuiDataGrid-columnHeaders': {
                                    backgroundColor: '#f0f0f0',
                                    textAlign: 'center',
                                },
                                '& .MuiDataGrid-columnHeaderTitleContainer': {
                                    justifyContent: 'center',
                                },
                                '& .MuiDataGrid-columnHeader': {
                                    textAlign: 'center',
                                },
                                '& .MuiDataGrid-columnHeaderGroup': {
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                },
                            }}
                        />
                    )}
                </div>
            </div>
            {/* Rest of the component remains unchanged */}
            <Dialog
                open={showReportDialog}
                onClose={() => setShowReportDialog(false)}
                PaperProps={{
                    sx: {
                        width: '80%',
                        maxWidth: 'none',
                        margin: 'auto'
                    }
                }}
                fullWidth
                maxWidth={false}
            >
                <DialogContent>
                    {reportLoading ? (
                        <div className={Styles.loadingContainer}>
                            <CircularProgress size={40} style={{ color: '#1450a0' }} />
                            <Typography variant="body1" style={{ marginTop: '1em' }}>
                                Loading student report...
                            </Typography>
                        </div>
                    ) : (
                        <AssessmentReportCard
                            reportDetails={reportDetails}
                            handleReporPreviewClose={() => {
                                setShowReportDialog(false);
                                setError('')
                            }}
                            error={error}
                            setError={setError}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </div >
    );
};